import React, { useState } from 'react';
import AuthCard from './components/AuthCard';
import { eulaVersion } from './utils/eula';

const MINIMUM_ORGANIZATION_NAME_LENGTH = 4;
const MAXIMUM_ORGANIZATION_NAME_LENGTH = 255;
const ORGANIZATION_NAME_LENGTH_MESSAGE =
  `Organization name must be between ${MINIMUM_ORGANIZATION_NAME_LENGTH} and ${MAXIMUM_ORGANIZATION_NAME_LENGTH} characters.`;

export default function CreateAccountPage({ onSubmit, onSignIn, onForgot }) {
  const [name, setName] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const passwordMismatch = confirmPassword.length > 0 && password !== confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedOrganizationName = String(organizationName || '').trim();

    if (password !== confirmPassword) {
      setError('');
      return;
    }
    if (trimmedOrganizationName.length < MINIMUM_ORGANIZATION_NAME_LENGTH) {
      setError(ORGANIZATION_NAME_LENGTH_MESSAGE);
      return;
    }
    if (trimmedOrganizationName.length > MAXIMUM_ORGANIZATION_NAME_LENGTH) {
      setError(ORGANIZATION_NAME_LENGTH_MESSAGE);
      return;
    }

    setBusy(true);
    setError('');
    setWarning('');
    try {
      const result = await onSubmit?.({ name, email, password, organizationName: trimmedOrganizationName, eulaVersion });
      if (result?.warningMessage?.length) {
        setWarning(result.warningMessage);
      } else if (result && typeof result === 'object' && result.error === true) {
        const respBody = result.respBody;
        const message =
          typeof respBody === 'string'
            ? respBody
            : respBody && typeof respBody === 'object'
              ? respBody.message
              : null;
        setError(message || 'An unknown error occurred.');
        setSent(false);
        return;
      } else if (result?.respBody) {
        setError(result?.respBody?.message || 'An unknown error occurred.');
        setSent(false);
        return;
      }
      setSent(true);
    } catch (e2) {
      const status = e2?.status;
      if (status === 400) setError('Please check your details and try again.');
      else if (status === 409) setError('That organization name is already taken.');
      else setError('Could not reach backend.');
      setSent(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthCard
      pageTestId="page-signup"
      cardTestId="signup-form"
      ariaLabel="Create account"
      title="Create your account"
      subtitle="Access Retail ID in seconds"
    >
      <form className="login-form" onSubmit={handleSubmit}>
          <label className="label">Full name</label>
          <input
            className="input"
            placeholder="Samira Khan"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            disabled={busy}
            data-testid="signup-name"
          />

          <label className="label">Organization</label>
          <input
            className="input"
            placeholder="Acme Cannabis Co"
            value={organizationName}
            onChange={(e) => setOrganizationName(e.target.value)}
            required
            maxLength={MAXIMUM_ORGANIZATION_NAME_LENGTH}
            disabled={busy}
            data-testid="signup-organization"
          />

          <label className="label">Email</label>
          <input
            type="email"
            className="input"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={busy}
            data-testid="signup-email"
          />

          <label className="label">Password</label>
          <input
            type="password"
            className="input"
            placeholder="Create a password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={10}
            disabled={busy}
            data-testid="signup-password"
          />

          <label className="label">Confirm password</label>
          <input
            type="password"
            className={`input ${passwordMismatch || error ? 'error' : ''}`}
            placeholder="Re-enter your password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            minLength={10}
            aria-invalid={passwordMismatch || Boolean(error)}
            disabled={busy}
            data-testid="signup-confirm-password"
          />
          {passwordMismatch && <div className="error-text" data-testid="signup-password-mismatch">Passwords do not match. Please try again.</div>}
          <label className="label">
            Please acknowledge you have read and agree to the User License Agreement before using the application.
          </label>
          <a
            href={`/eula/metrcid-${eulaVersion}.pdf`}
            target="_blank"
            rel="noopener noreferrer"
            className="label"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 4 }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="9" y1="13" x2="15" y2="13"/>
              <line x1="9" y1="17" x2="15" y2="17"/>
              <polyline points="9 9 10 9"/>
            </svg>
            View User License Agreement
          </a>
          <label className="label" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 0 }}>
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              required
              disabled={busy}
              data-testid="signup-agree"
            />
            I agree to the User License Agreement.
          </label>

          <button
            type="submit"
            className="button cta"
            style={{ width: '100%', marginTop: 8, ...(!agreed || busy ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}
            disabled={!agreed || busy}
            data-testid="signup-submit"
          >
            {busy ? 'Creating…' : 'Create Account'}
          </button>
        </form>

        {sent && (
          <div
            className="alert info"
            role="status"
            style={{ marginTop: -4, color: 'white', borderStyle: 'solid', borderWidth: 3, borderColor: 'orange' }}
            data-testid="signup-sent"
          >
            A verification link has been sent to {email || 'your email address'}. Check your inbox to complete setup.
          </div>
        )}

        {error && (
          <div
            className="alert"
            role="status"
            style={{ marginTop: -4, color: 'white', backgroundColor: 'maroon' }}
            data-testid="signup-error"
          >
            Error: {error}
          </div>
        )}

        {warning && (
          <div
            className="alert"
            role="status"
            style={{ marginTop: -4, color: 'white', backgroundColor: 'orange' }}
            data-testid="signup-warning"
          >
            {warning}
          </div>
        )}

        <div className="login-links" aria-label="Auth links">
          <button type="button" className="text-link" onClick={onSignIn} data-testid="create-account-back-to-login">Back to sign in</button>
          <button type="button" className="text-link" onClick={onForgot} data-testid="create-account-forgot-password">Forgot password?</button>
        </div>
    </AuthCard>
  );
}
